import type { Node } from './ast';
import { evaluate, type Scope } from './evaluate';
import { parse } from './parse';

export interface EvaluationResult {
  readonly value: number | null;
  readonly error: string | null;
}

const cache = new Map<string, Node | string>();

/** Parses `source` once and remembers it: the syntax tree, or the parse error's message. */
export function compile(source: string): Node | string {
  const cached = cache.get(source);
  if (cached !== undefined) return cached;

  const result = parse(source);
  const compiled = result.ok ? result.node : result.error.message;
  cache.set(source, compiled);
  return compiled;
}

/** Evaluates a compiled expression, turning a thrown error or a non-finite result into a message. */
export function evaluateCompiled(node: Node, scope: Scope): EvaluationResult {
  try {
    const value = evaluate(node, scope);
    if (!Number.isFinite(value)) return { value: null, error: 'Result is not a finite number' };
    return { value, error: null };
  } catch (error) {
    return { value: null, error: error instanceof Error ? error.message : String(error) };
  }
}
